import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useNavigate } from "react-router";
import type { Widget, WidgetTemplateListItem } from "../types";
import type { UserRole } from "@/modules/membership";
import { getFallbackPreview } from "../constants/widget-templates";
import IconRecycleBinThrow from "@/components/icons/icon-recycle-bin-throw";
import IconCheck from "@/components/icons/icon-check";
import Tooltip from "@/components/ui/tooltip";

export interface WidgetGridItemProps {
	widget: Widget;
	templates?: WidgetTemplateListItem[] | null;
	onDelete?: (id: string) => void;
	onEdit?: (id: string) => void;
	onStatusChange?: (id: string, status: string) => void;
	userRole?: UserRole | null;
}

function formatDate(dateString: string) {
	const date = new Date(dateString);
	return date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

function StatusBadge({ status, onStatusChange, widgetId }: { status: string; onStatusChange?: (id: string, status: string) => void; widgetId: string }) {
	const isPublished = status === "published";
	const isUnpublished = status === "unpublished";

	const getStatusColor = () => (isPublished ? "text-green-700" : "text-gray-500");
	const getStatusBgColor = () => (isPublished ? "bg-green-500" : "bg-gray-400");
	const getStatusLabel = () => (isPublished ? "Published" : "Unpublished");

	if (!onStatusChange) {
		return (
			<span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-md ${getStatusColor()}`}>
				<span className={`w-1.5 h-1.5 rounded-full ${getStatusBgColor()}`} />
				{getStatusLabel()}
			</span>
		);
	}

	return (
		<DropdownMenu.Root>
			<DropdownMenu.Trigger asChild>
				<button type="button" className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-md cursor-pointer transition-colors hover:bg-gray-100 ${getStatusColor()}`}>
					<span className={`w-1.5 h-1.5 rounded-full ${getStatusBgColor()}`} />
					{getStatusLabel()}
					<svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
					</svg>
				</button>
			</DropdownMenu.Trigger>
			<DropdownMenu.Portal>
				<DropdownMenu.Content className="bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[120px]" sideOffset={5}>
					<DropdownMenu.Item className="flex items-center justify-between px-3 py-2 text-sm cursor-pointer outline-none hover:bg-gray-50" onSelect={() => onStatusChange(widgetId, "published")}>
						<span className="text-gray-700">Published</span>
						{isPublished && <IconCheck className="w-4 h-4 text-brand" />}
					</DropdownMenu.Item>
					<DropdownMenu.Item className="flex items-center justify-between px-3 py-2 text-sm cursor-pointer outline-none hover:bg-gray-50" onSelect={() => onStatusChange(widgetId, "unpublished")}>
						<span className="text-gray-700">Unpublished</span>
						{isUnpublished && <IconCheck className="w-4 h-4 text-brand" />}
					</DropdownMenu.Item>
				</DropdownMenu.Content>
			</DropdownMenu.Portal>
		</DropdownMenu.Root>
	);
}

export default function WidgetGridItem({ widget, templates, onDelete, onEdit, onStatusChange, userRole }: WidgetGridItemProps) {
	const { id, status, created_at, title, display_type, template, template_version, blocked_by_plan } = widget;
	const matchedTemplate = templates?.find((t) => t.template === template && t.version === template_version);
	const templateLabel = matchedTemplate?.label;
	const previewImageUrl = matchedTemplate?.preview_image_url;
	const fallbackPreview = getFallbackPreview(template);
	const navigate = useNavigate();
	const isAdmin = userRole === "admin";

	const displayTitle = title || "Untitled Widget";

	const getTypeLabel = () => {
		switch (display_type) {
			case "single":
				return "Single";
			case "collection":
				return "Collection";
			case "aggregation":
				return "Aggregation";
			default:
				return "Widget";
		}
	};

	const getTypeColor = () => {
		switch (display_type) {
			case "single":
				return "text-purple-700 bg-purple-50";
			case "collection":
				return "text-blue-700 bg-blue-50";
			case "aggregation":
				return "text-emerald-700 bg-emerald-50";
			default:
				return "text-gray-700 bg-gray-100";
		}
	};

	return (
		<div
			className={`rounded-xl border transition-all overflow-hidden cursor-pointer ${blocked_by_plan ? "bg-gray-50 border-gray-200" : "bg-white border-gray-200 hover:shadow-sm hover:border-gray-300"}`}
			onClick={blocked_by_plan ? undefined : () => onEdit?.(id)}
		>
			{/* Preview image */}
			<div className={`h-36 w-full bg-gray-50 overflow-hidden ${blocked_by_plan ? "opacity-75" : ""}`}>
				{previewImageUrl ? (
					<img src={previewImageUrl} alt={templateLabel || template} className="w-full h-full object-cover" />
				) : fallbackPreview ? (
					<div className="w-full h-full flex items-center justify-center p-4">{fallbackPreview}</div>
				) : (
					<div className="w-full h-full flex items-center justify-center">
						<span className="text-xs text-gray-400">Preview</span>
					</div>
				)}
			</div>

			{/* Details */}
			<div className={`p-4 ${blocked_by_plan ? "opacity-75" : ""}`}>
				<div className="flex items-start justify-between gap-2 mb-2">
					<div className="min-w-0">
						<h3 className="text-sm font-medium text-primary truncate">{displayTitle}</h3>
						{templateLabel && <p className="text-xs text-tertiary mt-0.5 truncate">({templateLabel})</p>}
					</div>
					<span className={`inline-flex items-center justify-center shrink-0 px-2 py-0.5 text-xs font-medium rounded ${getTypeColor()}`}>{getTypeLabel()}</span>
				</div>

				<div className="flex items-center justify-between mt-3">
					<span className="text-xs text-tertiary">Created {formatDate(created_at)}</span>
					<div onClick={(e) => e.stopPropagation()}>
					{blocked_by_plan ? (
						<span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-md text-gray-400">
							<span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
							Disabled
						</span>
					) : (
						<StatusBadge status={status} onStatusChange={onStatusChange} widgetId={id} />
					)}
					</div>
				</div>
			</div>

{blocked_by_plan && (
				<div className="flex items-center gap-1 px-4 pb-4">
					{isAdmin ? (
						<Tooltip content="This widget is not available for your current plan. Click to upgrade.">
							<button
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									navigate(`/${window.location.pathname.split("/")[1]}/billing`);
								}}
								className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
							>
								<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
									<path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
								</svg>
								<span>Upgrade</span>
							</button>
						</Tooltip>
					) : (
						<Tooltip content="This widget is not available for your current plan. Please upgrade to enable it.">
							<span className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500">
								<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
									<path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
								</svg>
								<span>Disabled</span>
							</span>
						</Tooltip>
					)}
				</div>
				)}

				{/* Actions */}
			{onDelete && (
				<div className={`flex items-center gap-1 px-4 pb-4 ${blocked_by_plan ? "" : "pt-3 border-t border-gray-100"}`}>
					<button type="button" onClick={(e) => { e.stopPropagation(); onDelete(id); }} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors" aria-label="Delete widget">
						<IconRecycleBinThrow className="w-4 h-4" />
					</button>
				</div>
			)}
		</div>
	);
}
